import { Injectable, signal } from '@angular/core';

export type ConfirmDialogTone = 'danger' | 'primary';

export interface ConfirmDialogOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` para ações destrutivas: botão vermelho e foco inicial em "Cancelar". */
  tone?: ConfirmDialogTone;
}

export interface ActiveConfirmDialog extends Required<Omit<ConfirmDialogOptions, 'message'>> {
  message: string;
}

/**
 * Confirmação no estilo do sistema, substituta de `window.confirm`.
 * Uso: `if (!(await this.confirmDialog.confirm({ title: '...', tone: 'danger' }))) return;`
 * O componente `app-confirm-dialog` (montado uma vez no App) exibe o pedido ativo.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  private readonly _active = signal<ActiveConfirmDialog | null>(null);
  readonly active = this._active.asReadonly();

  private resolver: ((result: boolean) => void) | null = null;

  confirm(options: ConfirmDialogOptions): Promise<boolean> {
    // Um novo pedido encerra o anterior como cancelado.
    this.settle(false);

    const tone = options.tone ?? 'primary';
    this._active.set({
      title: options.title,
      message: options.message ?? '',
      confirmLabel: options.confirmLabel ?? (tone === 'danger' ? 'Excluir' : 'Confirmar'),
      cancelLabel: options.cancelLabel ?? 'Cancelar',
      tone,
    });

    return new Promise<boolean>((resolve) => {
      this.resolver = resolve;
    });
  }

  /** Fecha o diálogo ativo com a resposta do usuário. */
  settle(result: boolean): void {
    const resolve = this.resolver;
    this.resolver = null;
    this._active.set(null);
    resolve?.(result);
  }
}
